import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiRequest } from '../utils/auth';
import { Loader2 } from 'lucide-react';

export default function Analysis() {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChartData();
  }, []);

  const fetchChartData = async () => {
    try {
      const data = await apiRequest('/api/analytics/charts');
      setChartData(data);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#0A2540', '#1E4D7B', '#2E6BA8', '#4A8FD4', '#66A8E0', '#8BC4F0'];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  if (error || !chartData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <p className="text-red-500">{error || 'No data available'}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="analysis-page">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-[#0A2540]">Analysis Center</h2>
          <p className="text-sm text-gray-600 mt-1">Real-time business analytics and insights from your data</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border-gray-200">
            <p className="text-sm text-gray-500">Total RFQs</p>
            <p className="text-2xl font-bold text-[#0A2540]">{chartData.summary?.total_rfqs || 0}</p>
          </Card>
          <Card className="p-4 border-gray-200">
            <p className="text-sm text-gray-500">Total Customers</p>
            <p className="text-2xl font-bold text-[#0A2540]">{chartData.summary?.total_customers || 0}</p>
          </Card>
          <Card className="p-4 border-gray-200">
            <p className="text-sm text-gray-500">Onboarding Customers</p>
            <p className="text-2xl font-bold text-[#0A2540]">{chartData.summary?.onboarding_customers || 0}</p>
          </Card>
          <Card className="p-4 border-gray-200">
            <p className="text-sm text-gray-500">Total PO Value</p>
            <p className="text-2xl font-bold text-[#0A2540]">₹{(chartData.summary?.total_po_value || 0).toLocaleString('en-IN')}</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card className="p-6 border-gray-200">
            <h3 className="text-lg font-semibold text-[#0A2540] mb-4">RFQ Status Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.status_distribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {chartData.status_distribution?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6 border-gray-200">
            <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Category Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.category_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0A2540" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 mb-6">
          <Card className="p-6 border-gray-200">
            <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Monthly RFQ Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.monthly_rfq_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="received" fill="#0A2540" name="Received" radius={[4, 4, 0, 0]} />
                <Bar dataKey="submitted" fill="#2E6BA8" name="Submitted" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pending" fill="#FCD34D" name="Pending" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <Card className="p-6 border-gray-200">
          <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Monthly Sales Trend (in Lakhs ₹)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.monthly_sales}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
              <Tooltip formatter={(value) => [`₹${value} L`, 'Sales']} />
              <Line type="monotone" dataKey="value" stroke="#0A2540" strokeWidth={3} dot={{ r: 6, fill: '#0A2540' }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </Layout>
  );
}
